/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiResponse } from '../../types/apiTypes';
import {
  Kiosk,
  CreateKioskPayload,
  UpdateKioskPayload,
  UpdateAppearanceConfigPayload,
} from '../../types/kioskTypes';
import { Location } from '../../types/locationTypes';

export interface KiosksState {
  kiosks: ApiResponse<Kiosk>;
  selectedKiosk: Kiosk | null;
  kioskLocations: Location[];
  fetchKiosksLoading: boolean;
  describeKioskLoading: boolean;
  createKioskLoading: boolean;
  updateKioskLoading: boolean;
  deleteKioskLoading: boolean;
  getKioskLocationsLoading: boolean;
  updateKioskLocationsLoading: boolean;
  createAppearanceConfigLoading: boolean;
  updateAppearanceConfigLoading: boolean;
  error: string | null;
}

const initialState: KiosksState = {
  kiosks: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  selectedKiosk: null,
  kioskLocations: [],
  fetchKiosksLoading: false,
  describeKioskLoading: false,
  createKioskLoading: false,
  updateKioskLoading: false,
  deleteKioskLoading: false,
  getKioskLocationsLoading: false,
  updateKioskLocationsLoading: false,
  createAppearanceConfigLoading: false,
  updateAppearanceConfigLoading: false,
  error: null,
};

type KiosksLoadingKey =
  | 'fetchKiosksLoading'
  | 'describeKioskLoading'
  | 'createKioskLoading'
  | 'updateKioskLoading'
  | 'deleteKioskLoading'
  | 'getKioskLocationsLoading'
  | 'updateKioskLocationsLoading'
  | 'createAppearanceConfigLoading'
  | 'updateAppearanceConfigLoading';

const kiosksSlice = createSlice({
  name: 'kiosks',
  initialState,
  reducers: {
    fetchKiosks(
      state,
      _action: PayloadAction<{ orgId: number; locationId?: number }>,
    ) {
      state.error = null;
    },
    describeKiosk(
      state,
      _action: PayloadAction<{
        orgId: number;
        kioskId: number;
        locationId?: number;
      }>,
    ) {
      state.error = null;
    },
    createKiosk(
      _state,
      _action: PayloadAction<{
        orgId: number;
        kiosk: Partial<CreateKioskPayload>;
      }>,
    ) {},
    updateKiosk(
      _state,
      _action: PayloadAction<{
        orgId: number;
        kioskId: number;
        kiosk: Partial<UpdateKioskPayload>;
      }>,
    ) {},
    deleteKiosk(
      _state,
      _action: PayloadAction<{ orgId: number; kioskId: number }>,
    ) {},
    getKioskLocations(
      _state,
      _action: PayloadAction<{ orgId: number; kioskId: number }>,
    ) {},
    updateKioskLocations(
      _state,
      _action: PayloadAction<{
        orgId: number;
        kioskId: number;
        locations: number[];
      }>,
    ) {},
    createAppearanceConfig(
      _state,
      _action: PayloadAction<{
        orgId: number;
        kioskId: number;
        appearanceConfig: Partial<UpdateAppearanceConfigPayload>;
      }>,
    ) {},
    updateAppearanceConfig(
      _state,
      _action: PayloadAction<{
        orgId: number;
        kioskId: number;
        appearanceConfig: Partial<UpdateAppearanceConfigPayload>;
      }>,
    ) {},

    setKiosks(state, action: PayloadAction<ApiResponse<Kiosk>>) {
      state.kiosks = action.payload;
    },
    setSelectedKiosk(state, action: PayloadAction<ApiResponse<Kiosk>>) {
      state.selectedKiosk = action.payload.data[0] || null;
    },
    setKioskLocations(state, action: PayloadAction<ApiResponse<Location>>) {
      state.kioskLocations = action.payload.data;
    },

    clearKiosks(state) {
      state.kiosks = initialState.kiosks;
    },
    clearSelectedKiosk(state) {
      state.selectedKiosk = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearKiosksState() {
      return initialState;
    },

    setLoading(
      state,
      action: PayloadAction<{ key: KiosksLoadingKey; value: boolean }>,
    ) {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const {
  fetchKiosks,
  describeKiosk,
  createKiosk,
  updateKiosk,
  deleteKiosk,
  getKioskLocations,
  updateKioskLocations,
  createAppearanceConfig,
  updateAppearanceConfig,
  clearKiosks,
  clearSelectedKiosk,
  clearError,
  clearKiosksState,
} = kiosksSlice.actions;

export const kiosksActions = kiosksSlice.actions;
export default kiosksSlice.reducer;
